import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { getCommission, isConnected } from '../services/deriv';
import { getCommissionBreakdown, getCommissionRange, summarizeCommission } from '../services/commission';

import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import CommissionCard from '../components/CommissionCard';
import CommissionChart from '../components/CommissionChart';
import AppShareDonut from '../components/AppShareDonut';
import TopApps from '../components/TopApps';
import AllApps from './AllApps';
import { useCurrency } from '../hooks/useCurrency';
import { useAppStats } from '../hooks/useAppStats';
import { useTheme } from '../hooks/useTheme';

const TAB_TITLES = { overview: 'Overview', apps: 'All Apps' };

export default function Dashboard({ onLogout }) {
  const [today, setToday] = useState(0);
  const [todayTxns, setTodayTxns] = useState(0);
  const [todayClients, setTodayClients] = useState(0);
  const [thisMonth, setThisMonth] = useState(0);
  const [thisMonthTxns, setThisMonthTxns] = useState(0);
  const [thisMonthClients, setThisMonthClients] = useState(0);
  const [lastMonth, setLastMonth] = useState(0);
  const [lastMonthTxns, setLastMonthTxns] = useState(0);
  const [lastMonthClients, setLastMonthClients] = useState(0);
  const [custom, setCustom] = useState(null);
  const [customTxns, setCustomTxns] = useState(0);
  const [customClients, setCustomClients] = useState(0);

  const [chartData, setChartData] = useState([]);
  const [chartTitle, setChartTitle] = useState('Monthly Commission');
  const [chartTab, setChartTab] = useState('monthly');
  const [chartLoading, setChartLoading] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [customLoading, setCustomLoading] = useState(false);

  const [activeMainTab, setActiveMainTab] = useState('overview');
  const [activeApps, setActiveApps] = useState([]);
  const [selectedAppId, setSelectedAppId] = useState(null);

  // Local currency based on visitor's IP location
  const { code: localCode, rate: localRate } = useCurrency();

  // Shared per-app stats — powers both "Top Performing Apps" here and the All Apps tab
  const appStatsData = useAppStats();

  const [theme, setTheme] = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Data loaders
  const loadActiveApps = useCallback(async () => {
    if (!isConnected()) return; // no session yet — skip silently
    try {
      const { from, to } = getCommissionRange('thisMonth');
      const res = await getCommission(from, to);
      const breakdown = getCommissionBreakdown(res);
      setActiveApps(
        breakdown
          .filter((a) => a.app_markup_usd > 0)
          .map((a) => ({ app_id: a.app_id, commission: a.app_markup_usd }))
          .sort((a, b) => b.commission - a.commission)
      );
    } catch (error) {
      console.error('Failed to load active apps:', error);
    }
  }, []);

  const loadDefaultCards = useCallback(async () => {
    if (!isConnected()) return; // no session yet — skip silently
    setCardsLoading(true);
    try {
      const [todayRange, monthRange, lastMonthRange] = [
        getCommissionRange('today'),
        getCommissionRange('thisMonth'),
        getCommissionRange('lastMonth'),
      ];

      // Fetch sequentially so the dashboard cards take priority and do not
      // trip the commission endpoint's burst rate limit.
      const settle = async (range) => {
        try {
          return { status: 'fulfilled', value: await getCommission(range.from, range.to) };
        } catch (reason) {
          return { status: 'rejected', reason };
        }
      };
      const monthResult = await settle(monthRange);
      const lastMonthResult = await settle(lastMonthRange);
      const todayResult = await settle(todayRange);

      if (todayResult.status === 'fulfilled') {
        const todayRes = todayResult.value;
        const todayData = summarizeCommission(getCommissionBreakdown(todayRes), selectedAppId);
        setToday(todayData.commission);
        setTodayTxns(todayData.transactions);
        setTodayClients(selectedAppId ? todayData.clients : todayRes?.app_markup_statistics?.total_client_count || 0);
      } else {
        console.warn('Today commission is unavailable:', todayResult.reason);
      }

      if (monthResult.status === 'fulfilled') {
        const monthRes = monthResult.value;
        const monthData = summarizeCommission(getCommissionBreakdown(monthRes), selectedAppId);
        setThisMonth(monthData.commission);
        setThisMonthTxns(monthData.transactions);
        setThisMonthClients(selectedAppId ? monthData.clients : monthRes?.app_markup_statistics?.total_client_count || 0);
      } else {
        console.warn('Current month commission is unavailable:', monthResult.reason);
      }

      if (lastMonthResult.status === 'fulfilled') {
        const lastRes = lastMonthResult.value;
        const lastData = summarizeCommission(getCommissionBreakdown(lastRes), selectedAppId);
        setLastMonth(lastData.commission);
        setLastMonthTxns(lastData.transactions);
        setLastMonthClients(selectedAppId ? lastData.clients : lastRes?.app_markup_statistics?.total_client_count || 0);
      } else {
        console.warn('Previous month commission is unavailable:', lastMonthResult.reason);
      }
    } catch (error) {
      // Keep showing the last successfully loaded values rather than
      // overwriting good data with a misleading $0.00 on a transient error.
      if (!error?.message?.includes('No API session')) {
        console.error('Failed to load dashboard cards:', error);
      }
    } finally { setCardsLoading(false); }
  }, [selectedAppId]);

  const loadMonthlyChart = useCallback(async () => {
    if (!isConnected()) return;
    setChartLoading(true);
    try {
      const ref = dayjs();
      const months = await Promise.all(
        Array.from({ length: ref.month() + 1 }, (_, i) => {
          const label = dayjs().month(i).format('MMM');
          const from = ref.year(ref.year()).month(i).startOf('month').format('YYYY-MM-DD HH:mm:ss');
          const to = ref.year(ref.year()).month(i).endOf('month').format('YYYY-MM-DD HH:mm:ss');
          return getCommission(from, to)
            .then((res) => ({ label, value: summarizeCommission(getCommissionBreakdown(res), selectedAppId).commission }))
            .catch(() => ({ label, value: 0 }));
        })
      );
      setChartData(months);
      setChartTitle(selectedAppId ? `Monthly - App #${selectedAppId}` : 'Monthly Commission');
    } catch (error) {
      console.error('Failed to load monthly chart:', error);
    } finally { setChartLoading(false); }
  }, [selectedAppId]);

  const loadDailyChart = useCallback(async () => {
    if (!isConnected()) return;
    setChartLoading(true);
    try {
      const ref = dayjs();
      const days = ref.daysInMonth();
      const elapsed = Math.min(days, ref.date(), 30);
      const results = await Promise.all(
        Array.from({ length: elapsed }, (_, i) => {
          const d = i + 1;
          const from = ref.date(d).startOf('day').format('YYYY-MM-DD HH:mm:ss');
          const to = ref.date(d).endOf('day').format('YYYY-MM-DD HH:mm:ss');
          return getCommission(from, to)
            .then((res) => ({ label: `${d}`, value: summarizeCommission(getCommissionBreakdown(res), selectedAppId).commission }))
            .catch(() => ({ label: `${d}`, value: 0 }));
        })
      );
      setChartData(results);
      setChartTitle(selectedAppId ? `Daily - App #${selectedAppId}` : 'Daily Commission');
    } catch (error) {
      console.error('Failed to load daily chart:', error);
    } finally { setChartLoading(false); }
  }, [selectedAppId]);

  // Effects
  useEffect(() => {
    loadActiveApps();
  }, [loadActiveApps]);

  useEffect(() => {
    loadDefaultCards();
  }, [selectedAppId, loadDefaultCards]);

  useEffect(() => {
    if (cardsLoading) return;
    if (chartTab === 'monthly') loadMonthlyChart();
    else if (chartTab === 'daily') loadDailyChart();
  }, [selectedAppId, chartTab, cardsLoading, loadMonthlyChart, loadDailyChart]);

  // Handlers
  const handleChartTabChange = (tab) => {
    setChartTab(tab);
  };

  const checkCustom = async (from, to) => {
    setCustomLoading(true);
    try {
      const res = await getCommission(from, to);
      setCustom(res.app_markup_statistics?.total_app_markup_usd || 0);
      setCustomTxns(res.app_markup_statistics?.total_transactions_count || 0);
      setCustomClients(res.app_markup_statistics?.total_client_count || 0);
    } catch (error) {
      console.error('Failed to load custom commission range:', error);
    } finally {
      setCustomLoading(false);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar
        activeTab={activeMainTab}
        onTabChange={setActiveMainTab}
        onLogout={onLogout}
        theme={theme}
        onThemeChange={setTheme}
        menuOpen={mobileMenuOpen}
        onCloseMenu={() => setMobileMenuOpen(false)}
      />

      <div className="app-main">
        <Header
          title={TAB_TITLES[activeMainTab]}
          theme={theme}
          onThemeChange={setTheme}
          onOpenMenu={() => setMobileMenuOpen(true)}
          onDateRangeChange={checkCustom}
          activeApps={activeApps}
          selectedAppId={selectedAppId}
          onAppSelect={setSelectedAppId}
          checking={customLoading}
        />

        <div className="app-content">
          {customLoading && (
            <div className="loading-overlay" role="status" aria-live="polite">
              <div className="loader-panel">
                <span className="loader-ring" />
                <div className="loader-bars" aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <i />
                </div>
                <strong>Loading commission</strong>
                <p>Checking the selected date range</p>
              </div>
            </div>
          )}

          {activeMainTab === 'overview' ? (
            <>
              <div className="cards-grid">
                <CommissionCard
                  title="Today"
                  variant="day"
                  value={today}
                  transactions={todayTxns}
                  clients={todayClients}
                  loading={cardsLoading}
                  localRate={localRate}
                  localCode={localCode}
                />
                <CommissionCard
                  title={dayjs().format('MMMM')}
                  variant="currentMonth"
                  value={thisMonth}
                  trend={lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth * 100).toFixed(1) : null}
                  transactions={thisMonthTxns}
                  clients={thisMonthClients}
                  loading={cardsLoading}
                  localRate={localRate}
                  localCode={localCode}
                />
                <CommissionCard
                  title={dayjs().subtract(1, 'month').format('MMMM')}
                  variant="previousMonth"
                  value={lastMonth}
                  transactions={lastMonthTxns}
                  clients={lastMonthClients}
                  loading={cardsLoading}
                  localRate={localRate}
                  localCode={localCode}
                />
                {custom !== null && (
                  <CommissionCard
                    title="Custom Range"
                    value={custom}
                    transactions={customTxns}
                    clients={customClients}
                    localRate={localRate}
                    localCode={localCode}
                  />
                )}
              </div>

              <div className="overview-grid">
                <CommissionChart
                  title={chartTitle}
                  data={chartData}
                  activeTab={chartTab}
                  onTabChange={handleChartTabChange}
                  loading={chartLoading}
                />
                <AppShareDonut
                  apps={activeApps}
                  loading={cardsLoading && activeApps.length === 0}
                  onViewAll={() => setActiveMainTab('apps')}
                />
              </div>

              <TopApps
                apps={appStatsData.apps}
                appStats={appStatsData.appStats}
                onViewAll={() => setActiveMainTab('apps')}
              />
            </>
          ) : (
            <AllApps
              apps={appStatsData.apps}
              appStats={appStatsData.appStats}
              loading={appStatsData.loading}
              totals={appStatsData.totals}
            />
          )}
        </div>
      </div>
    </div>
  );
}
